import { useState, useEffect, useCallback } from "react";
import { db } from "../../firebase/config";
import {
  collection,
  doc,
  setDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { useStudentAuth } from "./useStudentAuth";

export interface PracticeSession {
  id: string;
  songId: string;
  songTitle: string;
  mode: "learn" | "play";
  bpm: number;
  score: number;
  attempts: number;
  flowScore: number;
  duration: number; // segundos
  sessionLog: { chordIndex: number; detected: boolean; timestamp: number; clarity: number }[];
  createdAt: Timestamp;
}

export interface PracticeSessionInput {
  songId: string;
  songTitle: string;
  mode: "learn" | "play";
  bpm: number;
  score: number;
  attempts: number;
  flowScore: number;
  duration: number;
  sessionLog: { chordIndex: number; detected: boolean; timestamp: number; clarity: number }[];
}

export function usePracticeSessions() {
  const { user } = useStudentAuth();
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.uid;

  useEffect(() => {
    if (!userId) {
      setSessions([]);
      setLoading(false);
      return;
    }

    const colRef = collection(db, "userSongbooks", userId, "practiceSessions");
    const q = query(colRef, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      const items: PracticeSession[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<PracticeSession, "id">),
      }));
      setSessions(items);
      setLoading(false);
    });

    return unsub;
  }, [userId]);

  const saveSession = useCallback(
    async (input: PracticeSessionInput) => {
      if (!userId) return;
      const id = crypto.randomUUID();
      const ref = doc(db, "userSongbooks", userId, "practiceSessions", id);
      await setDoc(ref, {
        ...input,
        createdAt: serverTimestamp(),
      });
      return id;
    },
    [userId]
  );

  return { sessions, loading, saveSession };
}
